import React, { useState } from "react";
import {
  Chip,
  Autocomplete,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import { format } from "date-fns";
import { enUS } from "@mui/x-date-pickers/locales";
import {
  StringOperator,
  NumericOperator,
  DateOperator,
  BooleanOperator,
  ListOperator,
} from "../../services/api/types/users/users";

export type AttributeValueType = string | number | boolean | string[] | null;

const commonLocaleText = {
  ...enUS,
  fieldMonthPlaceholder: (params: any) =>
    params.contentType === "letter" ? "MMM" : "MM",
};

export interface FieldConfig {
  dataType: string;
  attributeKey: string;
  attributeName?: string;
  isRequired?: boolean;
  isLineManager?: boolean;
}

type SX = SxProps<Theme>;

export interface DynamicFieldRendererSx {
  root?: SX;
  label?: SX;
  textField?: SX;
  inputRoot?: SX;
  datePicker?: SX;
  autocomplete?: SX;
  chip?: SX;
  switchRow?: SX;
  switchLabel?: SX;
}

export interface DynamicFieldRendererProps {
  config: FieldConfig;
  value: AttributeValueType;
  onChange: (value: AttributeValueType) => void;
  error?: string;
  disabled?: boolean;
  mode?: "form" | "filter"; // 'form' for simple forms, 'filter' for advanced filters
  operator?:
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | "";
  // Predefined options for list type
  options?: { key: string; value: string }[];
  // Date picker state for filters
  openDatepicker?: boolean;
  onOpenDatepicker?: () => void;
  onCloseDatepicker?: () => void;
  openStartDatepicker?: boolean;
  onOpenStartDatepicker?: () => void;
  onCloseStartDatepicker?: () => void;
  openEndDatepicker?: boolean;
  onOpenEndDatepicker?: () => void;
  onCloseEndDatepicker?: () => void;
  openFormDatepicker?: boolean;
  onOpenFormDatepicker?: () => void;
  onCloseFormDatepicker?: () => void;
  isReports?: boolean;
  usePipeSeparatedArrays?: boolean;
  sx?: DynamicFieldRendererSx;
}

export const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  config,
  value,
  onChange,
  error,
  disabled = false,
  mode = "form",
  operator,
  options = [],
  openDatepicker,
  onOpenDatepicker,
  onCloseDatepicker,
  openStartDatepicker,
  onOpenStartDatepicker,
  onCloseStartDatepicker,
  openEndDatepicker,
  onOpenEndDatepicker,
  onCloseEndDatepicker,
  openFormDatepicker,
  onOpenFormDatepicker,
  onCloseFormDatepicker,
  isReports = false,
  usePipeSeparatedArrays = false,
  sx,
}) => {
  const dataType = config.dataType.toLowerCase();
  const isDisabled = disabled || (mode === "filter" && !operator);

  const [pendingInputByKey, setPendingInputByKey] = useState<
    Record<string, string>
  >({});
  const pendingInput = pendingInputByKey[config.attributeKey] ?? "";
  const setPendingInput = (v: string) =>
    setPendingInputByKey((prev) => ({ ...prev, [config.attributeKey]: v }));

  const withTextFieldSx: SX = {
    ...sx?.textField,
    "& .MuiOutlinedInput-root": {
      ...((sx?.inputRoot as any) ?? {}),
    },
  };

  const withDatePickerSx: SX = {
    ...sx?.datePicker,
    "& .MuiPickersInputBase-root": {
      ...((sx?.datePicker as any) ?? {}),
    },
  };

  const withSwitchSx: SX = {
    ...sx?.switchLabel,
    "& .MuiFormControlLabel-root": {
      ...((sx?.switchLabel as any) ?? {}),
    },
  };

  const getChipsFromValue = (value: string): string[] => {
    if (!value) return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v);
  };

  // Helper for reports mode - uses pipe separator
  const getChipsFromValueReports = (value: string): string[] => {
    if (!value) return [];
    return value
      .split("|")
      .map((v) => v.trim())
      .filter((v) => v);
  };

  // Boolean field
  if (dataType === "boolean") {
    if (mode === "filter") {
      const boolValue = value === "true";
      return (
        <FormControlLabel
          control={
            <Switch
              disabled={isDisabled}
              checked={boolValue}
              onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            />
          }
          sx={{
            ...withSwitchSx,
            marginLeft: 0,
            "& .MuiFormControlLabel-label": { marginLeft: "8px" },
          }}
          label={boolValue ? "True" : "False"}
        />
      );
    }

    // Form mode
    return (
      <Stack alignItems="start">
        <Box sx={{ width: "100%" }}>
          <Box sx={{ height: 26 }} />
          <FormControlLabel
            label={config.attributeName || config.attributeKey}
            labelPlacement="start"
            control={
              <Switch
                disabled={isDisabled}
                checked={Boolean(value)}
                onChange={(_, v) => onChange(v)}
                required={config.isRequired}
              />
            }
            sx={{
              ...withSwitchSx,
              height: 44,
              border: `1px solid ${error ? "#d32f2f" : "#0000003A"}`,
              borderRadius: "8px",
              padding: "0 15px",
              gap: "32px",
              marginLeft: 0,

              "& .MuiFormControlLabel-asterisk": {
                color: "#d32f2f",
              },
            }}
          />
        </Box>
        <Typography
          color="error"
          variant="caption"
          sx={{
            minHeight: 23,
            visibility: error ? "visible" : "hidden",
          }}
        >
          {error}
        </Typography>
      </Stack>
    );
  }

  // Date field
  if (dataType === "date" || dataType === "datetime") {
    if (mode === "filter") {
      // Filter mode with BETWEEN operator support
      if (operator === DateOperator.BETWEEN) {
        const dateRange = value ? String(value).split("|") : ["", ""];
        const startDate = dateRange[0] ? dayjs(dateRange[0]) : null;
        const endDate = dateRange[1] ? dayjs(dateRange[1]) : null;

        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={commonLocaleText}
          >
            <Stack
              spacing={1}
              direction={isReports ? "row" : "column"}
              sx={{ width: "100%" }}
            >
              <DatePicker
                disabled={isDisabled}
                label="Start date"
                format="DD-MMM-YYYY"
                value={startDate}
                open={openStartDatepicker}
                onOpen={() => onOpenStartDatepicker?.()}
                onClose={() => onCloseStartDatepicker?.()}
                onChange={(date) => {
                  const start = date ? dayjs(date).format("DD-MMM-YYYY") : "";
                  const end = endDate ? endDate.format("DD-MMM-YYYY") : "";
                  onChange(`${start}|${end}`);
                }}
                slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                    placeholder: "Start date",
                    onClick: () => onOpenStartDatepicker?.(),
                    error: Boolean(error),
                    InputLabelProps: { shrink: true },
                    InputProps: {
                      readOnly: true,
                      sx: {
                        "& .MuiInputAdornment-root button": {
                          border: "none",
                          padding: 0,
                          background: "transparent",
                          "&:hover": { background: "transparent" },
                        },
                      },
                    },
                  },
                  openPickerButton: { sx: { border: "none" } },
                  openPickerIcon: { sx: { width: "15px" } },
                }}
                sx={{
                  ...withDatePickerSx,
                  "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                  "&. MuiPickersInputBase-root": {
                    flexDirection: "row-reverse",
                    padding: 0,
                    gap: "10px",
                  },
                  "&. MuiInputAdornment-root": { padding: 0 },
                }}
              />
              <DatePicker
                disabled={isDisabled}
                label="End date"
                format="DD-MMM-YYYY"
                value={endDate}
                open={openEndDatepicker}
                onOpen={() => onOpenEndDatepicker?.()}
                onClose={() => onCloseEndDatepicker?.()}
                onChange={(date) => {
                  const start = startDate
                    ? startDate.format("DD-MMM-YYYY")
                    : "";
                  const end = date ? dayjs(date).format("DD-MMM-YYYY") : "";
                  onChange(`${start}|${end}`);
                }}
                minDate={startDate || undefined}
                slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                    placeholder: "End date",
                    onClick: () => onOpenEndDatepicker?.(),
                    error: Boolean(error),
                    helperText: error,
                    InputLabelProps: { shrink: true },
                    InputProps: {
                      readOnly: true,
                      sx: {
                        "& .MuiInputAdornment-root button": {
                          border: "none",
                          padding: 0,
                          background: "transparent",
                          "&:hover": { background: "transparent" },
                        },
                      },
                    },
                  },
                  openPickerButton: { sx: { border: "none" } },
                  openPickerIcon: { sx: { width: "15px" } },
                }}
                sx={{
                  ...withDatePickerSx,
                  "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                  "&. MuiPickersInputBase-root": {
                    flexDirection: "row-reverse",
                    padding: 0,
                    gap: "10px",
                  },
                  "&. MuiInputAdornment-root": { padding: 0 },
                }}
              />
            </Stack>
          </LocalizationProvider>
        );
      }

      // Single date picker for filter mode
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={commonLocaleText}
        >
          <DatePicker
            disabled={isDisabled}
            label="Select date"
            format="DD-MMM-YYYY"
            value={value ? dayjs(String(value)) : null}
            open={
              onOpenDatepicker || onCloseDatepicker ? openDatepicker : undefined
            }
            onOpen={() => onOpenDatepicker?.()}
            onClose={() => onCloseDatepicker?.()}
            onChange={(date) =>
              onChange(date ? dayjs(date).format("DD-MMM-YYYY") : "")
            }
            slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
            slotProps={{
              textField: {
                size: "small",
                variant: "outlined",
                placeholder: "Select date",
                onClick: () => onOpenDatepicker?.(),
                error: Boolean(error),
                helperText: error,
                InputLabelProps: { shrink: true },
                InputProps: {
                  readOnly: true,
                  sx: {
                    "& .MuiInputAdornment-root button": {
                      border: "none",
                      padding: 0,
                      background: "transparent",
                      "&:hover": { background: "transparent" },
                    },
                  },
                },
              },
              openPickerButton: { sx: { border: "none" } },
              openPickerIcon: { sx: { width: "15px" } },
            }}
            sx={{
              ...withDatePickerSx,
              "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
              "&. MuiPickersInputBase-root": {
                flexDirection: "row-reverse",
                padding: 0,
                gap: "10px",
              },
              "&. MuiInputAdornment-root": { padding: 0 },
            }}
          />
        </LocalizationProvider>
      );
    }

    // Form mode
    return (
      <Stack gap="6px">
        <Typography height={20}>
          {config.attributeName || config.attributeKey}
          {config.isRequired && (
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          )}
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          localeText={commonLocaleText}
        >
          <DatePicker
            disabled={isDisabled}
            format="dd-MMM-yyyy"
            value={value ? new Date(String(value)) : null}
            onChange={(v) => {
              const d = v instanceof Date ? v : (v as any)?.toDate?.();
              onChange(d ? format(d, "dd-MMM-yyyy") : "");
            }}
            open={
              onOpenFormDatepicker || onCloseFormDatepicker
                ? openFormDatepicker
                : undefined
            }
            onOpen={() => onOpenFormDatepicker?.()}
            onClose={() => onCloseFormDatepicker?.()}
            slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                error: !!error,
                helperText: error || " ",
                onClick: () => onOpenFormDatepicker?.(),
                InputLabelProps: {
                  sx: {
                    transform: "translate(35px, 11.5px) scale(1)",
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(15px, -9px) scale(0.75)",
                    },
                  },
                },
              },
              openPickerButton: {
                sx: {
                  border: "none",
                  "&&:hover": { background: "transparent" },
                },
              },
              openPickerIcon: { sx: { width: "15px" } },
            }}
            sx={{
              ...withDatePickerSx,
              "& .MuiPickersInputBase-root": {
                flexDirection: "row-reverse",
                padding: 0,
                gap: "10px",
                height: 44,
              },
              "& .MuiInputAdornment-root": { ml: 0 },
            }}
          />
        </LocalizationProvider>
      </Stack>
    );
  }

  // Number field
  if (
    dataType === "number" ||
    dataType === "integer" ||
    dataType === "numeric"
  ) {
    if (mode === "filter") {
      return (
        <TextField
          disabled={isDisabled}
          placeholder="Enter number"
          value={value || ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
              onChange(val);
            }
          }}
          sx={withTextFieldSx}
          variant="outlined"
          size="small"
          fullWidth
          type="text"
          error={Boolean(error)}
          helperText={error}
          slotProps={{
            input: {
              endAdornment: error && (
                <ErrorOutlineIcon color="error" fontSize="small" />
              ),
            },
          }}
        />
      );
    }

    // Form mode
    return (
      <Stack gap="6px">
        <Typography height={20}>
          {config.attributeName || config.attributeKey}
          {config.isRequired && (
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          )}
        </Typography>
        <TextField
          disabled={isDisabled}
          fullWidth
          size="small"
          required={config.isRequired}
          type="number"
          placeholder={`Add ${config.attributeName || config.attributeKey}`}
          value={(value as number | string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error || " "}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 44,
            },
          }}
          slotProps={{
            input: {
              endAdornment: error && (
                <ErrorOutlineIcon color="error" fontSize="small" />
              ),
            },
          }}
        />
      </Stack>
    );
  }

  // String field
  if (dataType === "string" || dataType === "text") {
    if (mode === "filter") {
      // Filter mode with chips inside the field for IN/NOT_IN/CONTAINS/NOT_CONTAINS/STARTS_WITH/ENDS_WITH operators
      if (
        operator === StringOperator.IN ||
        operator === StringOperator.NOT_IN ||
        operator === StringOperator.CONTAINS ||
        operator === StringOperator.NOT_CONTAINS ||
        operator === StringOperator.STARTS_WITH ||
        operator === StringOperator.ENDS_WITH
      ) {
        const currentValues = getChipsFromValueReports(String(value || ""));

        return (
          <Autocomplete
            disabled={isDisabled}
            multiple
            freeSolo
            fullWidth
            options={[]}
            open={false}
            value={currentValues}
            inputValue={pendingInput}
            onInputChange={(_, v) => setPendingInput(v)}
            onBlur={() => {
              if (pendingInput.trim()) {
                const normalized = [...currentValues, pendingInput.trim()].filter(Boolean);
                onChange(normalized.join("|"));
                setPendingInput("");
              }
            }}
            onChange={(_, newValue) => {
              const normalized = (newValue as string[])
                .map((v) => v.trim())
                .filter(Boolean);
              setPendingInput("");
              onChange(normalized.join("|"));
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option}-${index}`}
                  label={option}
                  sx={{
                    backgroundColor: "white",
                    padding: "4px 6px",
                    borderRadius: "8px",
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  currentValues.length > 0 ? "" : "Type and press Enter..."
                }
                error={Boolean(error)}
                helperText={error}
                sx={{
                  ...withTextFieldSx,
                  "& .MuiOutlinedInput-root": {
                    padding: "2px 6px",
                    minHeight: 37,
                    borderRadius: "8px",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    ...((sx?.inputRoot as any) ?? {}),
                    // Ensure field can grow with chips (override any fixed height from sx)
                    height: "auto",
                  },
                }}
              />
            )}
            slotProps={{
              popupIndicator: {
                sx: { display: "none" },
              },
            }}
          />
        );
      }

      // Regular string input for filter mode
      return (
        <TextField
          disabled={isDisabled}
          placeholder="Enter value"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          error={Boolean(error)}
          helperText={error}
          slotProps={{
            input: {
              endAdornment: error && (
                <ErrorOutlineIcon color="error" fontSize="small" />
              ),
            },
          }}
          sx={withTextFieldSx}
        />
      );
    }

    // Form mode
    // Handle isLineManager string fields with chip input (pipe separated
    if (config.isLineManager && dataType === "string") {
      const rawParts: string[] = Array.isArray(value)
        ? value
        : typeof value === "string" && value
          ? value
              .split("|")
              .map((v) => v.trim())
              .filter(Boolean)
          : [];
      const currentValues =
        pendingInput.trim() &&
        rawParts.length > 0 &&
        rawParts[rawParts.length - 1] === pendingInput.trim()
          ? rawParts.slice(0, -1)
          : rawParts;

      return (
        <Stack gap="6px">
          <Typography height={20}>
            {config.attributeName || config.attributeKey}
            {config.isRequired && (
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            )}
          </Typography>
          <Autocomplete
            disabled={isDisabled}
            multiple
            freeSolo
            options={[]}
            open={false}
            size="small"
            value={currentValues}
            inputValue={pendingInput}
            onInputChange={(_, v) => {
              setPendingInput(v);
              onChange([...currentValues, v.trim()].filter(Boolean));
            }}
            onChange={(_, newValue) => {
              setPendingInput("");
              onChange(newValue as string[]);
            }}
            renderInput={(params) => {
              const hasValues =
                currentValues.length > 0 || Boolean(pendingInput.trim());
              return (
                <TextField
                  {...params}
                  placeholder={
                    hasValues ? "" : "Insert a value and press enter"
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 44,
                      alignItems: "center",
                    },
                  }}
                  error={Boolean(error)}
                  helperText={error || " "}
                  required={config.isRequired}
                />
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  sx={{
                    backgroundColor: "white",
                    padding: "14px 6px",
                    borderRadius: "6px",
                  }}
                />
              ))
            }
            slotProps={{
              popupIndicator: {
                sx: { display: "none" },
              },
              clearIndicator: {
                sx: { display: "none" },
              },
            }}
          />
        </Stack>
      );
    }

    // Regular string input for form mode
    return (
      <Stack gap="6px">
        <Typography height={20}>
          {config.attributeName || config.attributeKey}
          {config.isRequired && (
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          )}
        </Typography>
        <TextField
          disabled={isDisabled}
          fullWidth
          size="small"
          required={config.isRequired}
          value={(value as string) ?? ""}
          placeholder={`Add ${config.attributeName || config.attributeKey}`}
          onChange={(e) => onChange(e.target.value)}
          error={!isDisabled && !!error}
          helperText={!isDisabled && (error || " ")}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 44,
            },
          }}
          slotProps={{
            input: {
              endAdornment: !isDisabled && error && (
                <ErrorOutlineIcon color="error" fontSize="small" />
              ),
            },
          }}
        />
      </Stack>
    );
  }

  // List field (predefined options)
  if (dataType === "list") {
    if (mode === "filter") {
      const optionsList = options || [];
      // For reports mode (and any caller that opts in) use pipe separator; otherwise use comma
      const usePipeSeparator = isReports || usePipeSeparatedArrays;
      const currentValues = usePipeSeparator
        ? getChipsFromValueReports(String(value || ""))
        : getChipsFromValue(String(value || ""));
      const selectedOptions = currentValues
        .map((v) => optionsList.find((o) => o.key === v || o.value === v))
        .filter(Boolean) as { key: string; value: string }[];
      const showPlaceholder = selectedOptions.length === 0;
      return (
        <Autocomplete
          disabled={isDisabled}
          multiple
          fullWidth
          options={optionsList}
          freeSolo={false}
          getOptionLabel={(option) => option.value}
          isOptionEqualToValue={(option, val) => option.key === val.key}
          value={selectedOptions}
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            width: "100%",
          }}
          onChange={(_, newValue) => {
            const normalizedKeys = (
              newValue as { key: string; value: string }[]
            ).map((o) => o.key);
            // For reports mode with IN/NOT_IN operators, use pipe separator; otherwise use comma
            onChange(normalizedKeys.join(usePipeSeparator ? "|" : ", "));
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${option.key}-${index}`}
                label={option.value}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={showPlaceholder ? "Select values" : ""}
              variant="outlined"
              error={Boolean(error)}
              helperText={error}
              sx={{
                ...withTextFieldSx,
                "& .MuiOutlinedInput-root": {
                  padding: "2px 6px",
                  minHeight: 44,
                  borderRadius: "8px",
                  flexWrap: "wrap",
                  backgroundColor: "white",
                  ...((sx?.inputRoot as any) ?? {}),
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              }}
            />
          )}
          slotProps={{
            popupIndicator: {
              sx: { display: "none" },
            },
          }}
        />
      );
    }
  }
  // Default fallback
  return (
    <TextField
      disabled={isDisabled}
      placeholder="Enter value"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      error={Boolean(error)}
      helperText={error}
    />
  );
};
